import {useState} from "react";

// Hops rendered with the shared uptime UI <Table> (headerless), so the rounded
// container, row dividers and accessibility come from the design system. The
// collapse/expand state and destination labels are derived into the row list
// before it is handed to <Table>.
import {Button} from "@/components/button/Button.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, TextAlignment} from "@/components/table/Table.types.ts";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";
import {NetworkHop} from "@/lib/types/network.ts";

import {hopResponded, lastResponderIndex, tracerouteReached} from "./tracerouteHops.utils.ts";

import styles from "./TracerouteHopsTable.module.scss";

const formatRtt = (rtt_ms: (number | null)[]): string =>
  rtt_ms.map((sample) => (sample === null ? "*" : String(sample))).join(" / ");

const NO_RTT = "–";

type HopRow = {id: string; kind: "hop"; hop: NetworkHop; label?: "reached host" | "stopped here"};
type NoReplyRow = {id: string; kind: "noreply"; hop: NetworkHop; onCollapse?: () => void; groupSize: number};
type GapSummaryRow = {id: string; kind: "gap"; first: number; last: number; count: number; onExpand: () => void};
type HopTableRow = HopRow | NoReplyRow | GapSummaryRow;

// Group the flat hop list into responded-hop rows and runs of consecutive
// non-responding hops (collapsed to a summary row unless expanded), tagging the
// destination hop with its label.
const buildRows = (
  hops: NetworkHop[],
  expandedGaps: Record<number, boolean>,
  setExpandedGaps: (updater: (prev: Record<number, boolean>) => Record<number, boolean>) => void
): HopTableRow[] => {
  const reached = tracerouteReached(hops);
  const labelIdx = reached ? hops.length - 1 : lastResponderIndex(hops);

  const rows: HopTableRow[] = [];
  let i = 0;
  while (i < hops.length) {
    if (hopResponded(hops[i])) {
      rows.push({
        id: `h${hops[i].index}`,
        kind: "hop",
        hop: hops[i],
        label: i === labelIdx ? (reached ? "reached host" : "stopped here") : undefined,
      });
      i++;
      continue;
    }
    const start = i;
    while (i < hops.length && !hopResponded(hops[i])) i++;
    const gap = hops.slice(start, i);
    const key = gap[0].index;
    if (gap.length >= 2 && !expandedGaps[key]) {
      rows.push({
        id: `g${key}`,
        kind: "gap",
        first: gap[0].index,
        last: gap[gap.length - 1].index,
        count: gap.length,
        onExpand: () => setExpandedGaps((prev) => ({...prev, [key]: true})),
      });
    } else {
      gap.forEach((hop, idx) =>
        rows.push({
          id: `n${hop.index}`,
          kind: "noreply",
          hop,
          groupSize: gap.length,
          onCollapse:
            gap.length >= 2 && idx === gap.length - 1
              ? () => setExpandedGaps((prev) => ({...prev, [key]: false}))
              : undefined,
        })
      );
    }
  }
  return rows;
};

const SPECS: ColumnSpec<HopTableRow>[] = [
  {
    fieldName: "index",
    fieldTitle: "Hop",
    display: (ctx) => {
      const row = ctx!.row.original;
      return <span className={styles.hopIndex}>{row.kind === "gap" ? "" : row.hop.index}</span>;
    },
    meta: {columnClassName: styles.colHop, headerClassName: styles.colHop},
  },
  {
    fieldName: "host",
    fieldTitle: "Host",
    display: (ctx) => {
      const row = ctx!.row.original;
      if (row.kind === "hop") {
        const main = row.hop.host ?? row.hop.ip ?? "*";
        const sub = row.hop.host ? row.hop.ip : null;
        return (
          <div className={styles.hopHostBlock}>
            <EllipsisText className={styles.hopHost}>{main}</EllipsisText>
            {sub && <span className={styles.hopIp}>{sub}</span>}
          </div>
        );
      }
      if (row.kind === "noreply") {
        return (
          <span className={styles.hopContent}>
            <span className={styles.hopMuted}>Failed to acknowledge</span>
            {row.onCollapse && (
              <Button variant="linkBlue" size="text" typography="link" onClick={row.onCollapse}>
                Collapse {row.groupSize} hops
              </Button>
            )}
          </span>
        );
      }
      return (
        <span className={styles.hopContent}>
          <span className={styles.hopMuted}>
            Hops {row.first}–{row.last} did not reply
          </span>
          <Button variant="linkBlue" size="text" typography="link" onClick={row.onExpand}>
            Show All {row.count}
          </Button>
        </span>
      );
    },
  },
  {
    fieldName: "rtt",
    fieldTitle: "Round-trip time",
    display: (ctx) => {
      const row = ctx!.row.original;
      if (row.kind !== "hop") {
        return <span className={styles.hopRtt}>{NO_RTT}</span>;
      }
      return (
        <span className={styles.hopRttCell}>
          {row.label && <span className={styles.hopLabel}>{row.label}</span>}
          <span className={styles.hopRtt}>{row.hop.rtt_ms.length ? `${formatRtt(row.hop.rtt_ms)} ms` : NO_RTT}</span>
        </span>
      );
    },
    meta: {columnClassName: styles.hopRttCol, headerClassName: styles.colRtt, headerTextAlignment: TextAlignment.End},
  },
];

export const TracerouteHopsTable = ({hops}: {hops: NetworkHop[]}) => {
  const [expandedGaps, setExpandedGaps] = useState<Record<number, boolean>>({});
  const rows = buildRows(hops, expandedGaps, setExpandedGaps);
  return (
    <Table<HopTableRow>
      rows={rows}
      specs={SPECS}
      getRowId={(row) => row.id}
      headerClassName="visually-hidden"
      fixedWidth
      minWidth="22rem"
    />
  );
};
